import React, { useEffect, useRef, useState } from 'react'
import ImageGallery from 'react-image-gallery'
import 'react-image-gallery/styles/scss/image-gallery.scss'
import Lightbox from 'yet-another-react-lightbox'
import 'yet-another-react-lightbox/styles.css'
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen'
import Slideshow from 'yet-another-react-lightbox/plugins/slideshow'
import Counter from 'yet-another-react-lightbox/plugins/counter'
import { useDispatch } from 'react-redux'
import { axiosInstance, baseURL } from '../../api'
import { trackException } from '../../store/app-insights/reducer'
import {
  ItemImageSliderProps,
  ItemImageSliderVariant,
} from './item-image-slider.types'
import { useItemImageSliderStyles } from './item-image-slider.styles'

type GalleryImage = { original: string; thumbnail: string }

const buildPublicUrls = (imageId: number): GalleryImage => ({
  original: `${baseURL}listed-items/images/${imageId}/large.jpg`,
  thumbnail: `${baseURL}listed-items/images/${imageId}/small.jpg`,
})

const buildOwnerUrls = (imageId: number): GalleryImage => ({
  original: `listed-items/images/${imageId}/large.jpg`,
  thumbnail: `listed-items/images/${imageId}/small.jpg`,
})

const mapImages = (
  images: number[],
  variant: ItemImageSliderVariant
): GalleryImage[] =>
  images.map((id) =>
    variant === 'public' ? buildPublicUrls(id) : buildOwnerUrls(id)
  )

const ItemImageSliderSlidePublic: React.FC<{ original: string }> = ({
  original,
}) => {
  const classes = useItemImageSliderStyles()
  const imageAreaClass = `${classes.imageArea} ${classes.imageAreaPublic}`
  return (
    <div className={imageAreaClass}>
      <div className={classes.imgBgBlock}>
        <div
          style={{ backgroundImage: `url(${original})` }}
          className={classes.imageHolder}
        >
          {' '}
        </div>
      </div>
    </div>
  )
}

const ItemImageSliderSlideOwner: React.FC<{ original: string }> = ({
  original,
}) => {
  const classes = useItemImageSliderStyles()
  const ref = useRef<HTMLDivElement>(null)
  const dispatch = useDispatch()

  useEffect(() => {
    const objectUrl: { value?: string } = {}
    axiosInstance
      .get(original, { responseType: 'blob' })
      .then(({ data }) => data as Blob)
      .then((blob: Blob) => {
        objectUrl.value = URL.createObjectURL(blob)
        const el = ref.current
        if (el) el.style.backgroundImage = `url(${objectUrl.value})`
      })
      .catch((e) =>
        dispatch(trackException({ e, source: 'ItemImageSliderGetSlide' }))
      )
    return () => {
      if (objectUrl.value) URL.revokeObjectURL(objectUrl.value)
    }
  }, [original, dispatch])

  const imageAreaClass = `${classes.imageArea} ${classes.imageAreaOwnerLoading}`
  return (
    <div className={imageAreaClass}>
      <div className={classes.imgBgBlock}>
        <div ref={ref} className={classes.imageHolder} />
      </div>
    </div>
  )
}

const ItemImageSliderThumbPublic: React.FC<{ thumbnail: string }> = ({
  thumbnail,
}) => {
  const classes = useItemImageSliderStyles()
  return (
    <div
      className={classes.imageThumb}
      style={{ backgroundImage: `url(${thumbnail})` }}
    />
  )
}

const ItemImageSliderThumbOwner: React.FC<{ thumbnail: string }> = ({
  thumbnail,
}) => {
  const classes = useItemImageSliderStyles()
  const ref = useRef<HTMLDivElement>(null)
  const dispatch = useDispatch()

  useEffect(() => {
    const objectUrl: { value?: string } = {}
    axiosInstance
      .get(thumbnail, { responseType: 'blob' })
      .then((response) => response.data as Blob)
      .then((blob: Blob) => {
        objectUrl.value = URL.createObjectURL(blob)
        const el = ref.current
        if (el) el.style.backgroundImage = `url(${objectUrl.value})`
      })
      .catch((e) =>
        dispatch(trackException({ e, source: 'ItemImageSliderGetThumb' }))
      )
    return () => {
      if (objectUrl.value) URL.revokeObjectURL(objectUrl.value)
    }
  }, [thumbnail, dispatch])

  return <div className={classes.imageThumb} ref={ref} />
}

export const ItemImageSlider: React.FC<ItemImageSliderProps> = ({
  images,
  variant,
}) => {
  const classes = useItemImageSliderStyles()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [openLightBox, setOpenLightBox] = useState(false)

  const galleryItems = mapImages(images, variant)
  const lightBoxSlides = galleryItems.map((item) => ({ src: item.original }))

  const isPublic = variant === 'public'

  return (
    <>
      <div className={classes.slider}>
        {isPublic && (
          <Lightbox
            index={currentIndex}
            open={openLightBox}
            close={() => setOpenLightBox(false)}
            slides={lightBoxSlides}
            plugins={[Fullscreen, Counter, Slideshow]}
            carousel={{ finite: images.length === 1 }}
          />
        )}
        <ImageGallery
          items={galleryItems}
          renderThumbInner={(item: GalleryImage) =>
            isPublic ? (
              <ItemImageSliderThumbPublic thumbnail={item.thumbnail} />
            ) : (
              <ItemImageSliderThumbOwner thumbnail={item.thumbnail} />
            )
          }
          showFullscreenButton={false}
          lazyLoad
          onClick={isPublic ? () => setOpenLightBox(true) : undefined}
          onSlide={isPublic ? setCurrentIndex : undefined}
          renderItem={(item: GalleryImage) =>
            isPublic ? (
              <ItemImageSliderSlidePublic original={item.original} />
            ) : (
              <ItemImageSliderSlideOwner original={item.original} />
            )
          }
          showPlayButton={false}
        />
      </div>
    </>
  )
}
